/**
 * CommunityReportsAdmin - Board review page for community concern reports
 */
import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import AdminSidebar from "../components/layout/AdminSidebar";
import { toast } from "sonner";
import {
  Camera, Eye, CheckCircle, XCircle, Clock,
  AlertTriangle, Sparkles, User, MapPin, Loader2
} from "lucide-react";

const STATUS_MAP = {
  submitted: { label: "New", color: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  reviewed: { label: "Reviewed", color: "bg-green-500/10 text-green-600 border-green-500/20" },
  investigating: { label: "Investigating", color: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
  resolved: { label: "Resolved", color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
  dismissed: { label: "Dismissed", color: "bg-gray-500/10 text-gray-600 border-gray-500/20" },
};

const CommunityReportsAdmin = () => {
  const { token } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("reviewed");
  const [boardNotes, setBoardNotes] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API}/community-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(res.data.reports || []);
    } catch (error) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, [token]);

  const openDetail = async (report) => {
    setSelected(report);
    setDetailOpen(true);
    setReviewStatus(report.status === "submitted" ? "reviewed" : report.status);
    setBoardNotes(report.board_notes || "");

    // Load image
    if (report.file_id) {
      try {
        const res = await axios.get(`${API}/community-reports/${report.report_id}/image`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        });
        setImageUrl(URL.createObjectURL(res.data));
      } catch {
        setImageUrl(null);
      }
    }
  };

  const handleReview = async () => {
    if (!selected) return;
    try {
      setReviewing(true);
      await axios.put(`${API}/community-reports/${selected.report_id}/review`, {
        status: reviewStatus,
        board_notes: boardNotes
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Report updated");
      setDetailOpen(false);
      fetchReports();
    } catch (error) {
      toast.error("Failed to update report");
    } finally {
      setReviewing(false);
    }
  };

  const newCount = reports.filter(r => r.status === "submitted").length;

  return (
    <AdminSidebar>
      <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="community-reports-admin">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
            <Camera className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-serif text-lg font-semibold">Community Reports</h1>
              <Badge variant="outline" className="text-violet-600 border-violet-200 bg-violet-50">BETA</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Homeowner-submitted community concerns {newCount > 0 && <Badge className="ml-1 bg-blue-500 text-white">{newCount} new</Badge>}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading reports...</div>
        ) : reports.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Camera className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No community reports yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {reports.map((report) => {
              const statusConfig = STATUS_MAP[report.status] || STATUS_MAP.submitted;
              return (
                <Card key={report.report_id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openDetail(report)} data-testid={`report-card-${report.report_id}`}>
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                          <Badge variant="outline" className="capitalize">{report.concern_type}</Badge>
                          <Badge variant="outline" className={`capitalize ${report.severity === "high" ? "text-red-600" : report.severity === "medium" ? "text-amber-600" : ""}`}>
                            {report.severity}
                          </Badge>
                          {report.is_beta && <Badge variant="outline" className="text-violet-600 text-xs">BETA</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{report.description || "No description"}</p>
                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><User className="w-3 h-3" />{report.reporter_name}</span>
                          {report.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{report.location}</span>}
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(report.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <Eye className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="report-detail-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-violet-600" />
                Community Report Details
              </DialogTitle>
              <DialogDescription>Review and take action on this report</DialogDescription>
            </DialogHeader>

            {selected && (
              <div className="space-y-4">
                {/* Image */}
                {imageUrl && (
                  <img src={imageUrl} alt="Report" className="w-full max-h-64 object-contain rounded-lg border bg-muted" data-testid="report-detail-image" />
                )}

                {/* Info */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Reporter:</span> <span className="font-medium">{selected.reporter_name}</span></div>
                  <div><span className="text-muted-foreground">Type:</span> <span className="font-medium capitalize">{selected.concern_type}</span></div>
                  <div><span className="text-muted-foreground">Severity:</span> <span className="font-medium capitalize">{selected.severity}</span></div>
                  <div><span className="text-muted-foreground">Date:</span> <span className="font-medium">{new Date(selected.created_at).toLocaleString()}</span></div>
                  {selected.location && <div className="col-span-2"><span className="text-muted-foreground">Location:</span> <span className="font-medium">{selected.location}</span></div>}
                </div>

                {selected.description && (
                  <div>
                    <Label className="text-muted-foreground">Description</Label>
                    <p className="text-sm mt-1">{selected.description}</p>
                  </div>
                )}

                {/* AI Analysis */}
                {selected.ai_analysis && (
                  <div className="p-3 bg-violet-50/50 border border-violet-200 rounded-lg">
                    <p className="text-xs font-medium text-violet-700 mb-1 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> AI Analysis
                    </p>
                    <p className="text-sm whitespace-pre-wrap">{selected.ai_analysis}</p>
                  </div>
                )}

                {/* Board Review */}
                <div className="border-t pt-4 space-y-3">
                  <Label className="font-semibold">Board Review</Label>
                  <Select value={reviewStatus} onValueChange={setReviewStatus}>
                    <SelectTrigger data-testid="review-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reviewed">Mark as Reviewed</SelectItem>
                      <SelectItem value="investigating">Under Investigation</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="dismissed">Dismiss</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea
                    value={boardNotes}
                    onChange={(e) => setBoardNotes(e.target.value)}
                    placeholder="Add board notes..."
                    rows={3}
                    data-testid="review-notes-input"
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
              <Button onClick={handleReview} disabled={reviewing} data-testid="review-submit-btn">
                {reviewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Update Report
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminSidebar>
  );
};

export default CommunityReportsAdmin;
